import { forwardRef, ReactElement } from 'react';

import Box from '@mui/material/Box';
import { Breakpoint, SxProps, Theme } from '@mui/material/styles';
import { styled, useThemeProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

type BreakpointTitleValue = string | undefined;

export type ResponsiveTitle = {
  [key in Breakpoint]?: BreakpointTitleValue;
};

export interface GratiActionBarProps {
  actions?: ReactElement | null;
  title?: string | ResponsiveTitle;
  variant?: 'top' | 'god' | 'bottom';
  sx?: SxProps<Theme>;
}

interface GratiActionBarOwnerState {
  variant: 'top' | 'god' | 'bottom';
}

const GratiActionBarRoot = styled('footer', {
  name: 'GratiActionBar',
  slot: 'root',
  overridesResolver: (_props, styles) => {
    return styles.root;
  },
})<{ ownerState: GratiActionBarOwnerState }>(() => ({}));

const BREAKPOINTS: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];

function getTitleAtBreakpoint(title: string | ResponsiveTitle, breakpoint: Breakpoint): string | undefined {
  if (typeof title === 'string') {
    return title;
  }

  // Find the title for this breakpoint or the closest smaller one
  const bpIndex = BREAKPOINTS.indexOf(breakpoint);
  for (let i = bpIndex; i >= 0; i--) {
    if (title[BREAKPOINTS[i]] !== undefined) {
      return title[BREAKPOINTS[i]];
    }
  }
  return undefined;
}

const GratiActionBar = forwardRef<HTMLElement, GratiActionBarProps>(function GratiActionBar(
  inProps,
  ref
) {
  const props = useThemeProps({ props: inProps, name: 'GratiActionBar' });
  const { actions, title, variant = 'top', sx } = props;

  if (!actions && !title) return null;

  const ownerState: GratiActionBarOwnerState = { variant };

  // For responsive titles, we render multiple Typography elements with display control
  const renderTitle = () => {
    if (!title) return null;

    if (typeof title === 'string') {
      return (
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
      );
    }

    // Responsive title - render one Typography per unique title value with display control
    const titlesByValue = new Map<string, Breakpoint[]>();

    for (const bp of BREAKPOINTS) {
      const titleValue = getTitleAtBreakpoint(title, bp);
      if (titleValue) {
        const bps = titlesByValue.get(titleValue) || [];
        bps.push(bp);
        titlesByValue.set(titleValue, bps);
      }
    }

    // Create display objects for each unique title
    const elements: ReactElement[] = [];
    titlesByValue.forEach((_breakpoints, titleValue) => {
      const display: { [key in Breakpoint]?: string } = {};
      for (const bp of BREAKPOINTS) {
        const currentTitle = getTitleAtBreakpoint(title, bp);
        display[bp] = currentTitle === titleValue ? 'block' : 'none';
      }
      elements.push(
        <Typography
          key={titleValue}
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, display }}
        >
          {titleValue}
        </Typography>
      );
    });

    // If no title at xs, add a spacer for flexGrow
    if (!getTitleAtBreakpoint(title, 'xs')) {
      const spacerDisplay: { [key in Breakpoint]?: string } = {};
      for (const bp of BREAKPOINTS) {
        spacerDisplay[bp] = getTitleAtBreakpoint(title, bp) ? 'none' : 'block';
      }
      elements.push(
        <Box key="spacer" sx={{ flexGrow: 1, display: spacerDisplay }} />
      );
    }

    return <>{elements}</>;
  };

  return (
    <GratiActionBarRoot ref={ref} ownerState={ownerState} sx={sx}>
      {renderTitle()}
      {actions}
    </GratiActionBarRoot>
  );
});

GratiActionBar.displayName = 'GratiActionBar';

export default GratiActionBar;
